import { isBlockSenderEnabled } from '@/composables/useFeatureFlag';
import type {
  ISettingsFilterItem,
  ISettingsParsedFilterItemQuery,
  ISettingsParsedFilterItem,
  ISettingsFilterItemQuery
} from '@/types/services/filter.service';

const parseFilterSize = (
  size: ISettingsFilterItemQuery['size']
): ISettingsParsedFilterItemQuery['size'] => {
  const value = size?.size?.toString() || '0';
  const key = size?.type === 'larger' ? 'larger' : 'smaller';
  let parsedValue = parseInt(value, 10);
  let step: ISettingsParsedFilterItemQuery['size']['step'];
  if (parsedValue < 1024) {
    step = 'B';
  } else if (parsedValue < 1024 * 1024) {
    step = 'KB';
    parsedValue = parsedValue / 1024;
  } else {
    step = 'MB';
    parsedValue = parsedValue / 1024 / 1024;
  }
  return {
    value: parsedValue,
    direction: parsedValue === 0 ? 'off' : key,
    step
  };
};

export const isBlockedSenderFilter = (filter: ISettingsFilterItem): boolean => {
  // If the block sender feature flag is disabled, we should not treat any filter as a blocked sender filter
  if (!isBlockSenderEnabled) {
    return false;
  }
  const { from, to, subject, listId, text, ha, size } = filter.query;
  const { seen, flag, delete: del, spam, mailbox, targets } = filter.action;

  const onlyFromInQuery =
    !!from && !to && !subject && !listId && !text && (ha === null || ha === false) && size === null;

  const onlySpamInAction =
    spam === true && !seen && !flag && !del && !mailbox && (!targets || targets.length === 0);

  return onlyFromInQuery && onlySpamInAction;
};

export const parseFilterStrings = (filter: ISettingsFilterItem): ISettingsParsedFilterItem => {
  const parsed: ISettingsParsedFilterItem = {
    ...filter,
    enabled: !filter.disabled,
    query: {
      ...filter.query,
      size: parseFilterSize(filter.query.size)
    },
    action: {
      ...filter.action,
      mailbox:
        (filter.metaData?.canMoveTo && filter.metaData?.mailbox
          ? filter.metaData?.mailbox
          : null) ??
        filter.action.mailbox ??
        null,
      targets: filter.action.targets ?? [],
      spam:
        filter.action.spam === true ? 'spam' : filter.action.spam === false ? 'notspam' : 'nothing'
    },
    metaData: filter.metaData ?? {}
  };

  return parsed;
};
